import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './languages';

/**
 * Auto-discover every locale namespace file under `./locales/<lng>/<ns>.json`.
 * Adding a new namespace or language is just dropping a JSON file in place —
 * no edit to this config required (keeps parallel translation work conflict-free).
 */
const modules = import.meta.glob<{ default: Record<string, unknown> }>('./locales/*/*.json', {
  eager: true,
});

const resources: Record<string, Record<string, Record<string, unknown>>> = {};

for (const path in modules) {
  // path looks like: ./locales/fr/landing.json
  const match = path.match(/\.\/locales\/([^/]+)\/([^/]+)\.json$/);
  if (!match) continue;
  const lng = match[1];
  const ns = match[2];
  if (!lng || !ns) continue;
  const mod = modules[path];
  if (!mod) continue;
  resources[lng] ??= {};
  resources[lng][ns] = mod.default;
}

const supportedLngs = SUPPORTED_LANGUAGES.map((l) => l.code);

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs,
    nonExplicitSupportedLngs: true,
    defaultNS: 'common',
    interpolation: {
      escapeValue: false, // React already escapes
    },
    detection: {
      // 'querystring' FIRST, and it is load-bearing for SEO, not a convenience.
      // The site serves fr/en/es from the same URL, so `<link rel="alternate"
      // hreflang>` has nothing to point at unless a URL can pin a language.
      // `?lang=xx` is that URL (see `components/seo/publicRoutes.ts`, which
      // owns the parameter name and explains why a query parameter rather than
      // an `/en/…` route prefix). A crawler — or a human clicking a translated
      // result — that lands on `?lang=en` and is served French would make the
      // hreflang annotation a lie, and this line is what stops that.
      // It sits ahead of localStorage on purpose: an explicit language in the
      // URL beats the visitor's last choice, and 'localStorage' in `caches`
      // then makes that choice stick for the rest of the visit.
      order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'aikka-lang',
    },
  });

// Keep <html lang="…"> in sync with the active language (SEO + accessibility).
const syncHtmlLang = (lng: string) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lng;
  }
};
syncHtmlLang(i18n.resolvedLanguage ?? DEFAULT_LANGUAGE);
i18n.on('languageChanged', syncHtmlLang);

export default i18n;
